import { useEnum } from "@/api/services/global/enum";
import { IconClose } from "@/assets/icons";
import { AutoFilledInput } from "@/features/company/components/AutoFilledInput";
import { BILLING_RECIPIENT_COLUMNS, BILLING_RECIPIENT_GRID_TEMPLATE } from "@/features/company/constants/billing-recipient-columns";
import { ZIP_CODE_PLACEHOLDER } from "@/features/company/constants/sections";
import { TCompanyForm } from "@/features/company/types/company-form";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { IconBox } from "@/modules/irohana-components/elements/IconBox";
import { Input } from "@/modules/irohana-components/elements/Input";
import { SelectNew } from "@/modules/irohana-components/elements/SelectNew";
import { icon } from "@/theme/colors";
import { Box, Stack } from "@mui/material";
import { FC } from "react";
import { Path, useFormContext } from "react-hook-form";

type TBillingRecipientRowProps = {
  index: number;
  onRemove: () => void;
};

/** One 請求先 row. Kept as its own component so each row runs its own 郵便番号 → 住所 lookup. */
export const BillingRecipientRow: FC<TBillingRecipientRowProps> = ({ index, onRemove }) => {
  const methods = useFormContext<TCompanyForm>();
  const { control } = methods;
  const { data: locationTypeOptions } = useEnum("ECompanyLocationType");
  const fieldName = (key: string) => `billingRecipients.${index}.${key}` as Path<TCompanyForm>;

  // 行ごとに 郵便番号 → 都道府県・市区町村 を自動入力する（他の行には影響しない）。
  useLocationInfo<TCompanyForm>({
    methods,
    names: { zipCode: fieldName("zipCode"), province: fieldName("prefecture"), municipality: fieldName("city") },
  });

  return (
    <Box display="grid" gridTemplateColumns={BILLING_RECIPIENT_GRID_TEMPLATE} gap="8px" alignItems="start" px="16px" py="8px">
      {BILLING_RECIPIENT_COLUMNS.map(({ key, label, required, readonly }) => (
        <FormField<TCompanyForm> key={key} name={fieldName(key)} control={control} label={label} required={required} noLabel fullWidth>
          {key === "locationType" ? (
            <SelectNew fullWidth options={locationTypeOptions} />
          ) : key === "zipCode" ? (
            // Matching 999-9999 triggers the row's own 住所 lookup, so full-width digits are normalised while typing.
            <Input fullWidth clearable transformToHalfWidth placeholder={ZIP_CODE_PLACEHOLDER} />
          ) : readonly ? (
            <AutoFilledInput />
          ) : (
            <Input fullWidth clearable />
          )}
        </FormField>
      ))}

      <Stack alignItems="center" justifyContent="center" height="40px">
        <IconBox onClick={onRemove} aria-label={`${index + 1}行目の請求先を削除`}>
          <IconClose color={icon.default} />
        </IconBox>
      </Stack>
    </Box>
  );
};
